/* Gunrange shop — predictive search in the search overlay
   (snippets/gr-search-overlay.liquid, layer "gr-search").

   As the visitor types (2+ characters, 250 ms after the last keystroke) the
   gr-predictive-search section is rendered by Shopify's Predictive Search
   API (the overlay's data-gr-suggest-url, i.e. routes.predictive_search_url)
   through GR.fetchSection, and its [data-gr-suggest] content replaces the
   results panel. The section's data-gr-status sentence goes to the live
   region. A newer keystroke aborts the older request, and closing the
   overlay aborts the one in flight (and a keystroke still waiting); the
   interrupted search runs again when the overlay reopens.

   If suggestions fail, the overlay says so (the error template) and Enter
   still submits the form to the search page. */
(() => {
  'use strict';

  const GR = window.GR;
  const overlay = document.getElementById('gr-search');
  if (!overlay) return;
  if (!GR || typeof GR.fetchSection !== 'function') {
    console.error('[gr-predictive-search] gr-shop.js has not loaded; the search form still submits to the search page.');
    return;
  }

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;
  const input = overlay.querySelector('input[name="q"]');
  const panel = overlay.querySelector('[data-gr-search-results]');
  const start = overlay.querySelector('[data-gr-search-start]');
  const status = overlay.querySelector('[data-gr-search-status]');
  const errorTemplate = overlay.querySelector('template[data-gr-search-error]');
  const endpoint = overlay.dataset.grSuggestUrl;
  if (!input || !panel || !start || !status || !endpoint) {
    console.error('[gr-predictive-search] The overlay markup is incomplete; suggestions are off.');
    return;
  }

  let controller = null;
  let interrupted = false; // closed before the typed term's results were in

  const showResults = (on) => {
    panel.hidden = !on;
    start.hidden = on;
  };

  const suggestUrl = (term) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
    return url.toString();
  };

  const run = async (term) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;
    overlay.setAttribute('aria-busy', 'true');
    try {
      const doc = await GR.fetchSection(suggestUrl(term), 'gr-predictive-search', { signal });
      if (signal.aborted) return;
      const content = doc.querySelector('[data-gr-suggest]');
      if (!content) throw new Error('The predictive search section rendered no [data-gr-suggest] content.');
      panel.replaceChildren(...Array.from(content.childNodes).map((node) => document.importNode(node, true)));
      status.textContent = content.dataset.grStatus || '';
      showResults(true);
      GR.layer.wire();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[gr-predictive-search] Suggestions failed; Enter still searches.', error);
      if (errorTemplate) panel.replaceChildren(errorTemplate.content.cloneNode(true));
      status.textContent = panel.querySelector('h2')?.textContent || '';
      showResults(true);
    } finally {
      if (!signal.aborted) overlay.removeAttribute('aria-busy');
    }
  };

  const suggest = GR.debounce(run, DEBOUNCE_MS);

  input.addEventListener('input', () => {
    const term = input.value.trim();
    if (term.length < MIN_CHARS) {
      suggest.cancel();
      if (controller) controller.abort();
      overlay.removeAttribute('aria-busy');
      status.textContent = '';
      showResults(false);
      return;
    }
    suggest(term);
  });

  overlay.addEventListener('gr:layer-open', () => {
    if (input.value) input.select();
    const term = input.value.trim();
    if (interrupted && term.length >= MIN_CHARS) run(term);
    interrupted = false;
  });

  overlay.addEventListener('gr:layer-close', () => {
    interrupted = suggest.pending || overlay.hasAttribute('aria-busy');
    suggest.cancel();
    if (controller) controller.abort();
    overlay.removeAttribute('aria-busy');
  });
})();
